import React, { useState, useEffect, FC, JSX } from "react";
import Button from "../ui/Button";
import Loader from "../ui/Loader";
import ConfirmationDialog from "../ui/ConfirmationDialog";
import Badge from "../ui/Badge";
import MultiSelect from "../ui/MultiSelect";
import Select from "../ui/Select";
import { UserRole, GetAllUsersQueryParams, Attachments, UserDetails, UserStatus } from "../../types/api";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import DatePicker from "../ui/DatePicker";
import userService from "../../services/user.service";
import authService from "../../services/auth.service";
import { usePagination } from "../../hooks/usePagination";
import Pagination from "../ui/pagination";

interface UserManagementTableProps {
  isAdminView?: boolean;
  pageTitle: string;
  pageDescription: string;
  showFilters?: {
    role?: boolean;
    isMobileVerified?: boolean;
    paymentStatus?: boolean;
    dateRange?: boolean;
    status?: boolean;
  };
  defaultItemsPerPage?: number;
  filterRole?: (UserRole | "All")[];
  tableView?: boolean;
  onSelect?: (userId: string) => void;
  extraFilters?: (user: UserDetails) => boolean;
  columns?: ('Action' | 'Profile' | 'Name' | 'Email' | 'Mobile' | 'Role' | 'Status' | 'Attachments')[];
}

const UserManagementTable: FC<UserManagementTableProps> = ({
  isAdminView = false,
  pageTitle,
  pageDescription,
  showFilters,
  filterRole: initialFilterRole = ["All"],
  tableView = true,
  defaultItemsPerPage,
  onSelect,
  extraFilters,
  columns = ['Action', 'Profile', 'Name', 'Email', 'Mobile', 'Role', 'Status'],
}): JSX.Element => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [allUsers, setAllUsers] = useState<Map<number,UserDetails[]>>(new Map().set(1,[]));
  const [filteredUsers, setFilteredUsers] = useState<UserDetails[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const [userToAction, setUserToAction] = useState<string | null>(null);
  const [actionType, setActionType] = useState<string | null>(null);
  const [actionReason, setActionReason] = useState<string>("");

  const [filterRole, setFilterRole] = useState<(UserRole | "All")[]>((searchParams.get("roles")?.split(",") as (UserRole | "All")[]) || initialFilterRole);
  const [filterMobileVerified, setFilterMobileVerified] = useState<boolean | "All">(searchParams.get("mobileVerified") === "true" ? true : searchParams.get("mobileVerified") === "false" ? false : "All");
  const [filterPaymentStatus, setFilterPaymentStatus] = useState<boolean | "All">(searchParams.get("paid") === "true" ? true : searchParams.get("paid") === "false" ? false : "All");
  const [filterStatus, setFilterStatus] = useState<UserStatus | "All">((searchParams.get("status") as UserStatus) || "active");
  const [fromDate, setFromDate] = useState<string | undefined>(searchParams.get("fromDate") || undefined);
  const [toDate, setToDate] = useState<string | undefined>(searchParams.get("toDate") || undefined);

  const [jobseekerAttachmentsMap, setJobseekerAttachmentsMap] = useState<Map<string, Attachments[] | null>>(new Map());
  const [attachmentLoadingMap, setAttachmentLoadingMap] = useState<Map<string, boolean>>(new Map());
  const [itemsPerPage, setItemsPerPage] = useState<number>(parseInt(searchParams.get("itemsPerPage") || "10"));
  const [totalPages, setTotalPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(parseInt(searchParams.get("page") || "1"));
  const [totalUsers, setTotalUsers] = useState<number>(0);

  const handleFilterChange = (filterName: string, value: any) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (value === undefined || value === null || (Array.isArray(value) && value.length === 0)) {
      newSearchParams.delete(filterName);
    } else {
      newSearchParams.set(filterName, Array.isArray(value) ? value.join(",") : value.toString());
    }
    newSearchParams.set("page", "1");
    setCurrentPage(1);
    setSearchParams(newSearchParams);
  };
  const handledDateChange = (fromDate:string | undefined , toDate: string | undefined) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (fromDate === undefined || fromDate === null || toDate === null || toDate === undefined ) {
      newSearchParams.delete("fromDate");
      newSearchParams.delete("toDate");
    } else {
      newSearchParams.set("fromDate", fromDate.split('T')[0]);
      newSearchParams.set("toDate", toDate.split('T')[0]);
    }
    newSearchParams.set("page", "1");
    setCurrentPage(1);
    setSearchParams(newSearchParams);
  };
  
  const fetchAllUsers = async (params: GetAllUsersQueryParams) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.getAllUsers(params);
      if (response.status === "success" && response.data && response.data.items) {
        setAllUsers(prevUsers => prevUsers.set(params.pageNumber,response.data.items));
        console.log("[test] setting current page to ", params.pageNumber)
        setCurrentPage(params.pageNumber);
        setTotalPages(Math.ceil(response.data.totalCount/response.data.pageSize ));
        setTotalUsers(response.data.totalCount);
      } else {
        setError(response.status || "Failed to fetch users");
      }
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("[test] non CurrentPage effect")
    setAllUsers(new Map().set(1,[]));
    const params: GetAllUsersQueryParams = { pageNumber: currentPage, pageSize: itemsPerPage };
    if (filterRole && !filterRole.includes("All")) {
      params.roles = filterRole.join(",") as UserRole;
    }
    if (filterMobileVerified !== "All" && filterMobileVerified !== undefined) {
      params.mobileVerified = filterMobileVerified;
    }
    if (filterPaymentStatus !== "All" && filterPaymentStatus !== undefined) {
      params.paid = filterPaymentStatus;
    }
    if (fromDate) {
      const date = new Date(fromDate);
      date.setHours(0, 0, 0, 0);
      params.fromDate = date.toISOString();
    }
    if (toDate) {
      const date = new Date(toDate);
      date.setHours(23, 59, 59, 999);
      params.toDate = date.toISOString();
    }
    fetchAllUsers(params);

  }, [filterRole, filterMobileVerified, filterPaymentStatus, fromDate, toDate, itemsPerPage, filterStatus]);

  useEffect(() => {
    console.log("[test] CurrentPage effect",currentPage, allUsers.get(currentPage) == null)
    if (allUsers.get(currentPage) == null){
      const params: GetAllUsersQueryParams = { pageNumber: currentPage, pageSize: itemsPerPage };
      if (filterRole && !filterRole.includes("All")) {
        params.roles = filterRole.join(",") as UserRole;
      }
      if (filterMobileVerified !== "All" && filterMobileVerified !== undefined) {
        params.mobileVerified = filterMobileVerified;
      }
      if (filterPaymentStatus !== "All" && filterPaymentStatus !== undefined) {
        params.paid = filterPaymentStatus;
      }
      if (fromDate) {
        const date = new Date(fromDate);
        date.setHours(0, 0, 0, 0);
        params.fromDate = date.toISOString();
      }
      if (toDate) {
        const date = new Date(toDate);
        date.setHours(23, 59, 59, 999);
        params.toDate = date.toISOString();
      }
      fetchAllUsers(params);
    }
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", currentPage.toString());
    setSearchParams(newSearchParams);
  }, [currentPage]);

  useEffect(() => {
    const roles = searchParams.get("roles");
    const mobileVerified = searchParams.get("mobileVerified");
    const paid = searchParams.get("paid");
    const status = searchParams.get("status");
    const from = searchParams.get("fromDate");
    const to = searchParams.get("toDate");
    const page = searchParams.get("page");

    setFilterRole(prev => roles ? (roles.split(",") as (UserRole | "All")[]) : initialFilterRole);
    setFilterMobileVerified(prev => mobileVerified === "true" ? true : mobileVerified === "false" ? false : 'All');
    setFilterPaymentStatus(prev =>  paid === "true" ? true : paid === "false" ? false : 'All');
    setFilterStatus(prev => status ? (status as UserStatus) : 'active');
    setFromDate(from || undefined);
    setToDate(to || undefined);
    setCurrentPage(prev => page ? parseInt(page) : prev);
  }, [searchParams]);
  const handleRefresh = () => {
    setAllUsers(new Map().set(1,[]));
    setCurrentPage(1);
    const params: GetAllUsersQueryParams = { pageNumber: 1, pageSize: itemsPerPage };
    if (filterRole && !filterRole.includes("All")) {
      params.roles = filterRole.join(",") as UserRole;
    }
    if (filterMobileVerified !== "All" && filterMobileVerified !== undefined) {
      params.mobileVerified = filterMobileVerified;
    }
    if (filterPaymentStatus !== "All" && filterPaymentStatus !== undefined) {
      params.paid = filterPaymentStatus;
    }
    if (fromDate) {
      const date = new Date(fromDate);
      date.setHours(0, 0, 0, 0);
      params.fromDate = date.toISOString();
    }
    if (toDate) {
      const date = new Date(toDate);
      date.setHours(23, 59, 59, 999);
      params.toDate = date.toISOString();
    }
    fetchAllUsers(params);
  };
  
  const handleActionClick = (
    userId: string,
    type: string
  ): void => {
    setUserToAction(userId);
    setActionType(type);
    setIsConfirmOpen(true);
  };

  const handleConfirmAction = async (): Promise<void> => {
    if (!userToAction || !actionType) return;

    setActionLoading(userToAction);
    try {
      console.log(`User ${userToAction} ${actionType.toLowerCase()}d with reason: ${actionReason}`);
    } catch (error: any) {
      console.error(`Error ${actionType.toLowerCase()} user:`, error);
    } finally {
      setActionLoading(null);
      setIsConfirmOpen(false);
      setUserToAction(null);
      setActionType(null);
      setActionReason("");
      handleRefresh();
    }
  };

  const handleGetAttachments = async (userId: string): Promise<void> => {
    setAttachmentLoadingMap((prev) => new Map(prev).set(userId, true));
    try {
      const response = await userService.getJobSeekerDetails(userId);
      if (response && response.data) {
        setJobseekerAttachmentsMap((prev) => new Map(prev).set(userId, response.data.attachments || []));
      } else {
        setJobseekerAttachmentsMap((prev) => new Map(prev).set(userId, []));
      }
    } catch (error) {
      console.error("Error fetching jobseeker attachments:", error);
      setJobseekerAttachmentsMap((prev) => new Map(prev).set(userId, null));
    } finally {
      setAttachmentLoadingMap((prev) => new Map(prev).set(userId, false));
    }
  };

  const handleCancelAction = (): void => {
    setIsConfirmOpen(false);
    setUserToAction(null);
    setActionType(null);
    setActionReason("");
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("itemsPerPage", newItemsPerPage.toString());
    newSearchParams.set("page", "1");
    setCurrentPage(1);
    setSearchParams(newSearchParams);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  const getPageNumbers = (): number[] => {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  useEffect(()=>{
    let users = allUsers.get(currentPage) || [];
    if (extraFilters) {
      users = users.filter(extraFilters);
    }
    if (filterStatus !== "All") {
      users = users.filter(user => user.status?.toLocaleLowerCase() === filterStatus);
    }
    setFilteredUsers(users);
    console.log("setting filterdUsers ", users, filterStatus)
  },[allUsers.get(currentPage), extraFilters, filterStatus])

  const showAnyFilter = showFilters?.role || showFilters?.isMobileVerified || showFilters?.paymentStatus || showFilters?.dateRange || showFilters?.status;
  const renderTableCell = (column: string, currentUser: UserDetails) => {
    switch (column) {
      case "Action":
        if (onSelect) {
          return (
            <Button onClick={() => onSelect(currentUser.userId)} size="sm">
              Select
            </Button>
          );
        }
        return ""
      case "Profile":
        return (
          <div className="flex">
            {currentUser.companyId && (
              <Button
                className="flex-none"
                variant="secondary"
                size="sm"
                onClick={() =>
                  navigate(`/${user?.role?.toLowerCase()}/companyProfile/${currentUser.companyId}`)
                }
              >
                View Company
              </Button>
            )}
            {(currentUser.role === "Jobseeker" || currentUser.role === "Intern") && (
              <Button
                className="flex-none ml-2"
                variant="secondary"
                size="sm"
                onClick={() =>
                  navigate(`/${user?.role?.toLowerCase()}/user/${currentUser.userId}`, { state: { user: currentUser } })
                }
              >
                View Profile
              </Button>
            )}
          </div>
        );
      case "Name":
        return `${currentUser.firstName} ${currentUser.lastName}`;
      case "Email":
        return currentUser.email;
      case "Mobile":
        return currentUser.mobileNumber;
      case "Role":
        return currentUser.role;
      case "Status":
        return (
          <Badge variant={currentUser.status && currentUser.status === "active" ? "success" : "danger"}>
            {currentUser.status}
          </Badge>
        );
      case "Attachments":
        return (
          currentUser.role === "Jobseeker" && (
            <div className="mt-4">
              {attachmentLoadingMap.get(currentUser.userId) ? (
                <div className="flex items-center justify-center">
                  <Loader size="sm" />
                  <span className="ml-2 text-gray-600">Loading attachments...</span>
                </div>
              ) : jobseekerAttachmentsMap.has(currentUser.userId) ? (
                jobseekerAttachmentsMap.get(currentUser.userId) && jobseekerAttachmentsMap.get(currentUser.userId)!.length > 0 ? (
                  <div className="flex flex-col space-y-2">
                    {jobseekerAttachmentsMap.get(currentUser.userId)!.map((attachment) => (
                      <a
                        key={attachment.attachmentId}
                        href={attachment.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline"
                      >
                        {attachment.originalName || `Attachment ${attachment.attachmentId}`}
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">No attachments found.</p>
                )
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleGetAttachments(currentUser.userId)}
                >
                  Get Attachments
                </Button>
              )}
            </div>
          )
        );
      default:
        return null;
    }
  };
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{pageTitle}</h1>
        <p className="text-gray-600 mt-2">{pageDescription}</p>
      </div>

      <div className="flex justify-start mb-4">
        <Button onClick={handleRefresh} loading={loading} disabled={loading}>
          Refresh
        </Button>
      </div>

      {showAnyFilter && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Filter Users
            </h3>
          <div className="flex flex-wrap items-center gap-4">
            {showFilters?.role && (
              <MultiSelect
                label="Role"
                placeholder="Select Role"
                options={[
                  { value: "Jobseeker", label: "Jobseeker" },
                  { value: "Intern", label: "Intern" },
                  { value: "Company", label: "Company Users" },
                  { value: "Admin", label: "Admin" },
                  { value: "Agent", label: "Agent" },
                ]}
                value={filterRole.includes("All") ? [] : filterRole || []}
                onChange={(value) =>
                  handleFilterChange("roles", value.length === 0 ? "All" : value)
                }
                className="w-full sm:w-2/5"
              />
            )}

            {showFilters?.isMobileVerified && (
              <Select
                label="Mobile Verified"
                placeholder="Select Status"
                options={[
                  { value: "All", label: "All" },
                  { value: "true", label: "Yes" },
                  { value: "false", label: "No" },
                ]}
                value={
                  filterMobileVerified === true
                    ? "true"
                    : filterMobileVerified === false
                    ? "false"
                    : "All"
                }
                onChange={(value) =>
                  handleFilterChange("mobileVerified", value)
                }
                className="w-full sm:w-2/5"
                native={true}
              />
            )}
            {showFilters?.paymentStatus && (
              <Select
                label="Payment Status"
                placeholder="Select Status"
                options={[
                  { value: "All", label: "All" },
                  { value: "true", label: "Paid" },
                  { value: "false", label: "Pending" },
                ]}
                value={filterPaymentStatus === true ? "true" : filterPaymentStatus === false ? "false" : "All"}
                onChange={(value) =>
                  handleFilterChange("paid", value)
                }
                className="w-full sm:w-2/5"
                native={true}
              />
            )}

            {showFilters?.dateRange && 
              <DatePicker
              label="Date Range"
              value={[fromDate, toDate]}
              withTime={false}
              max={new Date().toISOString().split('T')[0]}
              min={new Date("2025-10-17").toISOString().split('T')[0]}
              onChange={(val) =>{
                if(typeof val == 'object'){
                  handledDateChange(val[0],val[1]);
                }
              }} 
              placeholder="Select From Date"
              className="w-full sm:w-2/5"
              range={true}
              />
            }
            {(fromDate || toDate) && (
              <Button
              variant="danger"
              size="sm"
              onClick={() => {
                handledDateChange(undefined, undefined);
              }}
              className="self-end"
              >
              Clear Dates
              </Button>
            )}
            {showFilters?.status && (
              <Select
                label="Status"
                placeholder="Select Status"
                options={[
                  { value: "All", label: "All" },
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                ]}
                value={filterStatus.toLocaleLowerCase()}
                onChange={(value) =>
                  handleFilterChange("status", value)
                }
                className="w-full sm:w-2/5"
                native={true}
              />
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader size="lg" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-red-600">Error: {error}</div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 48 48"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No users found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Adjust your filters or check back later.
          </p>
        </div>
      ) : tableView ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((u: UserDetails) => (
                  <tr key={u.userId}>
                    {columns.map((column) => (
                      <td key={column} className="px-6 py-4 whitespace-nowrap">
                        {renderTableCell(column, u)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredUsers.map((u: UserDetails) => (
            <div
              key={u.userId}
              className="bg-white rounded-lg shadow border border-gray-200 p-6"
            >
              <div className=" justify-between items-start">
                <div className="flex-1">
                  <div className="items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      {u.firstName} {u.lastName}
                    </h3>
                    <div className="flex flex-wrap">
                      <Badge
                        className="flex-none m-1 ml-0"
                        variant={"default"}
                      >
                        {u.role}
                      </Badge>
                      {u.mobileVerified && (
                        <Badge
                        className="flex-none m-1"
                          variant="success">
                          Mobile Verified
                        </Badge>
                      )}
                      {u.paymentVerified && (
                        <Badge
                        className="flex-none m-1"
                          variant="success">
                          Paid User
                        </Badge>
                      )}
                      {!u.paymentVerified && u.role === "Jobseeker" && (
                        <Badge
                        className="flex-none m-1"
                          variant='warning'>
                          Payment Pending
                        </Badge>
                      )}
                      {u.emailVerified && (
                        <Badge 
                        className="flex-none m-2"
                          variant="success">
                          Email Verified
                        </Badge>
                      )}
                    </div>
                  </div>

                  {u.role === "Jobseeker" && (
                    <div className="mt-4">
                      {attachmentLoadingMap.get(u.userId) ? (
                        <div className="flex items-center justify-center">
                          <Loader size="sm" />
                          <span className="ml-2 text-gray-600">Loading attachments...</span>
                        </div>
                      ) : jobseekerAttachmentsMap.has(u.userId) ? (
                        jobseekerAttachmentsMap.get(u.userId) && jobseekerAttachmentsMap.get(u.userId)!.length > 0 ? (
                          <div className="flex flex-col space-y-2">
                            <p className="text-sm font-medium text-gray-700">Attachments:</p>
                            {jobseekerAttachmentsMap.get(u.userId)!.map((attachment) => (
                              <a
                                key={attachment.attachmentId}
                                href={attachment.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:underline"
                              >
                                {attachment.originalName || `Attachment ${attachment.attachmentId}`}
                              </a>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-600">No attachments found.</p>
                        )
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleGetAttachments(u.userId)}
                        >
                          Get Attachments
                        </Button>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-4 mt-4">
                    <div>
                      <p className="text-sm text-gray-600">
                        <strong>Email:</strong> {u.email || "N/A"}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Mobile:</strong> {u.mobileNumber || "N/A"}
                      </p>
                       <p className="text-sm text-gray-600  mb-4">
                        <strong>Created On:</strong>{" "}
                        {u.createDate
                          ? new Date(u.createDate).toLocaleDateString()
                          : "N/A"}
                      </p>
                      <div className="flex">
                        {u.companyId && (
                          <Button
                          className="flex-none"
                            variant='secondary'
                            size="sm"
                            onClick={() =>
                              navigate(`/${user?.role?.toLowerCase()}/companyProfile/${u.companyId}`)
                            }
                          >
                            View Company
                          </Button>
                        )}
                        {(u.role === "Jobseeker" || u.role === "Intern") && (
                          <Button
                            className="flex-none ml-2"
                            variant='secondary'
                            size="sm"
                            onClick={() =>
                              navigate(`/${user?.role?.toLowerCase()}/user/${u.userId}`, { state: { user: u } })
                            }
                          >
                            View Profile
                          </Button>
                        )}
                      </div>
                    </div>
                    
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
       <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalUsers / itemsPerPage)}
          totalItems={totalUsers}
          onPageChange={(page) => setCurrentPage(page)}
          onNextPage={goToNextPage}
          onPreviousPage={goToPreviousPage}
          getPageNumbers={getPageNumbers}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      <ConfirmationDialog
        isOpen={isConfirmOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmAction}
        title={`${actionType} User`}
        message={`Please provide a reason for ${actionType?.toLowerCase()} this user:`}
        confirmText={actionType === "Deactivate" ? "Deactivate" : "Activate"}
        variant={actionType === "Deactivate" ? "danger" : "info"}
        isLoading={actionLoading !== null}
      >
        <textarea
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-primary-lightGreen focus:border-primary-lightGreen"
          rows={4}
          placeholder={`Reason for ${actionType?.toLowerCase()}...`}
          value={actionReason}
          onChange={(e) => setActionReason(e.target.value)}
        ></textarea>
      </ConfirmationDialog>
    </div>
  );
};

export default UserManagementTable;